import { create } from 'zustand';
import { env } from '@/lib/env';
import { onStepUpRequired } from '@/lib/api';

/**
 * Step-up auth: high-risk actions require a recent re-authentication. We record
 * the timestamp of the last successful re-auth (performance.now based, monotonic)
 * and treat an action as elevated if it happened within STEPUP_WINDOW_MIN.
 *
 * The verification call hits the real `POST /api/auth/reauth` (the server mints a
 * short-lived step-up token); this store only tracks the elevation window for the
 * client-side gate/UX.
 */
interface StepUpState {
  lastElevatedAt: number | null;
  /** The server-minted step-up token from the last reauth — sent as the
   *  `x-step-up-token` header on actions the server enforces (e.g. impersonate). */
  token: string | null;
  markElevated: (token?: string) => void;
  clear: () => void;
}

export const useStepUpStore = create<StepUpState>((set) => ({
  lastElevatedAt: null,
  token: null,
  markElevated: (token) => set({ lastElevatedAt: performance.now(), token: token ?? null }),
  clear: () => set({ lastElevatedAt: null, token: null }),
}));

export function isElevated(): boolean {
  const last = useStepUpStore.getState().lastElevatedAt;
  if (last == null) return false;
  const windowMs = env.stepUpWindowMin * 60_000;
  return performance.now() - last <= windowMs;
}

/** The current step-up token if the elevation window is still open, else null.
 *  Prefer `stepUpHeaders()` in a mutation. */
export function getStepUpToken(): string | null {
  return isElevated() ? useStepUpStore.getState().token : null;
}

/**
 * FIX(admin-stepup-server-enforced): the headers every step-up-gated mutation
 * sends — `{ 'x-step-up-token': <token> }` while elevated, else undefined (the
 * server then refuses with STEP_UP_REQUIRED). Before: only impersonation sent
 * the token, copy-pasted inline. After: one helper for every gated call:
 * `apiClient.delete(url, { headers: stepUpHeaders() })`.
 */
export function stepUpHeaders(): Record<string, string> | undefined {
  const token = getStepUpToken();
  return token ? { 'x-step-up-token': token } : undefined;
}

// FIX(admin-stepup-server-enforced): the server refused our token (it expired
// between the prompt and the call, or the server restarted with a new secret):
// drop the elevation so the next attempt prompts for the password again.
// Before: the client kept believing it was elevated for the rest of its local
// window, so every retry skipped the prompt and failed the same way.
// After: cleared on the first refusal.
onStepUpRequired(() => useStepUpStore.getState().clear());
